import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import BottomNav from '@/components/BottomNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImageCropper } from '@/components/ImageCropper';
import { LogOut, Heart, MapPin, Briefcase, GraduationCap, Phone, ExternalLink, Sun, Moon, Globe, User, Check, Pencil, Star, Sparkles, Users, Link2, Camera, X, Search, AlertTriangle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  SHIO_OPTIONS, MBTI_OPTIONS, ZODIAC_OPTIONS, MARRIAGE_STATUS_OPTIONS,
  TUJUAN_OPTIONS, INCOME_RANGE_OPTIONS, ALIRAN_AGAMA_OPTIONS,
  FIELD_LINKS, FIELD_DESCRIPTIONS
} from '@/data/profileFields';
import { EXTENDED_FIELD_KEYS } from '@/data/extendedProfileFields';
import { ExtendedProfileEditor } from '@/components/ExtendedProfileEditor';
import { ExtendedProfileView } from '@/components/ExtendedProfileView';
import axios from 'axios';
import { toast } from 'sonner';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { token, member, claimedProfile, logout, updateMember, updateClaimedProfile, API } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [claimName, setClaimName] = useState('');
  const [selectedToClaim, setSelectedToClaim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [cropOpen, setCropOpen] = useState(false);
  const [cropIndex, setCropIndex] = useState(0);

  const [claimStep, setClaimStep] = useState(1);
  const [claimSearch, setClaimSearch] = useState('');
  const [debouncedClaimSearch, setDebouncedClaimSearch] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimResults, setClaimResults] = useState([]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Debounce the claim search input — avoids hammering the server on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedClaimSearch(claimSearch.trim()), 250);
    return () => clearTimeout(id);
  }, [claimSearch]);

  // Server-side search: fires whenever debounced query changes (and the dialog is on step 2).
  useEffect(() => {
    if (!showClaimDialog || claimStep !== 2) return;
    let cancelled = false;
    const run = async () => {
      setClaimLoading(true);
      try {
        const url = `${API}/members/search-unclaimed?q=${encodeURIComponent(debouncedClaimSearch)}&limit=50`;
        const res = await axios.get(url, { headers });
        if (!cancelled) setClaimResults(res.data.profiles || []);
      } catch (err) {
        console.error('[ProfilePage] search-unclaimed failed:', err);
        if (!cancelled) setClaimResults([]);
      }
      if (!cancelled) setClaimLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [API, headers, showClaimDialog, claimStep, debouncedClaimSearch]);

  const openPhotoCrop = (index) => {
    setCropIndex(index);
    setCropOpen(true);
  };

  const handleCroppedPhoto = async (base64) => {
    try {
      await axios.post(`${API}/members/update-photo`, { photo: base64, index: cropIndex }, { headers });
      toast.success(lang === 'en' ? 'Photo updated!' : 'Foto diperbarui!');
      fetchAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeletePhoto = async (index) => {
    try {
      await axios.delete(`${API}/members/delete-photo/${index}`, { headers });
      toast.success(lang === 'en' ? 'Photo removed' : 'Foto dihapus');
      fetchAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const fetchAccount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/members/profile`, { headers });
      updateMember(res.data.account);
      updateClaimedProfile(res.data.claimed_profile);
    } catch (err) { console.error('[ProfilePage] fetchAccount failed:', err); }
  }, [API, headers, updateMember, updateClaimedProfile]);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  const openClaimDialog = () => {
    setClaimStep(1);
    setClaimName('');
    setSelectedToClaim(null);
    setClaimSearch('');
    setDebouncedClaimSearch('');
    setClaimResults([]);
    setShowClaimDialog(true);
    // Actual fetching happens reactively via the useEffect on (showClaimDialog, claimStep, debouncedClaimSearch).
  };

  const handleClaim = async () => {
    if (!selectedToClaim || !claimName.trim()) {
      toast.error(lang === 'en' ? 'Select a profile and enter your name' : 'Pilih profil dan masukkan nama');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/members/claim`, { member_id: selectedToClaim.id, name: claimName.trim() }, { headers });
      toast.success(lang === 'en' ? 'Profile claimed!' : 'Profil diklaim!');
      setShowClaimDialog(false);
      fetchAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim');
    }
    setLoading(false);
  };

  const openEditDialog = () => {
    const seed = {
      city: claimedProfile?.city || '',
      country: claimedProfile?.country || '',
      province: claimedProfile?.province || '',
      education: claimedProfile?.education || '',
      birth_year: claimedProfile?.birth_year || '',
      shio: claimedProfile?.shio || '',
      mbti: claimedProfile?.mbti || '',
      zodiac: claimedProfile?.zodiac || '',
      preferred_places: claimedProfile?.preferred_places || '',
      aliran_agama: claimedProfile?.aliran_agama || '',
      hobby: claimedProfile?.hobby || '',
      topics_to_talk: claimedProfile?.topics_to_talk || '',
      marriage_status: claimedProfile?.marriage_status || '',
      tujuan_join: claimedProfile?.tujuan_join || '',
      kriteria_pasangan: claimedProfile?.kriteria_pasangan || '',
      income_range: claimedProfile?.income_range || '',
      tiktok: claimedProfile?.tiktok || '',
      facebook: claimedProfile?.facebook || '',
      instagram: claimedProfile?.instagram || '',
    };
    // Seed the 24 extended optional fields — preserve raw values (bool/int/null).
    EXTENDED_FIELD_KEYS.forEach(k => {
      seed[k] = claimedProfile?.[k] ?? null;
    });
    setEditForm(seed);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      // Build payload: keep everything the user touched, including cleared strings.
      // Only skip `undefined` / `null`. Empty string means "clear this field" and
      // must be sent so the DB can be updated (previous logic silently dropped
      // clears, which made fields feel "locked" once filled).
      const data = {};
      Object.entries(editForm).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        data[k] = v;
      });
      await axios.put(`${API}/members/update-profile`, data, { headers });
      toast.success(lang === 'en' ? 'Profile updated!' : 'Profil diperbarui!');
      setShowEditDialog(false);
      fetchAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setLoading(false);
  };

  const handleLogout = () => { logout(); navigate('/'); };
  const photos = claimedProfile?.photos?.filter(p => p) || [];
  const inputCls = "t-bg-input t-border h-10 rounded-lg text-sm px-4 t-text";
  const compactInputCls = "t-bg-input t-border h-9 rounded-lg text-xs px-3 t-text";

  const getMarriageLabel = (val) => {
    const opt = MARRIAGE_STATUS_OPTIONS.find(o => o.value === val);
    return opt ? (lang === 'en' ? opt.en : opt.id) : val;
  };
  const getTujuanLabel = (val) => {
    const opt = TUJUAN_OPTIONS.find(o => o.value === val);
    return opt ? (lang === 'en' ? opt.en : opt.id) : val;
  };

  const InfoItem = ({ label, value }) => {
    if (!value) return null;
    return (
      <div className="flex justify-between items-start py-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <span className="t-text-muted text-xs uppercase tracking-wider">{label}</span>
        <span className="t-text text-sm text-right max-w-[60%]">{value}</span>
      </div>
    );
  };

  // Flattened list of aliran agama options for the select (memoized — static data)
  const aliranOptions = useMemo(
    () => Object.entries(ALIRAN_AGAMA_OPTIONS).flatMap(([religion, sects]) => sects.map(s => `${religion} - ${s}`)),
    []
  );

  return (
    <div className="min-h-screen t-bg pb-24">
      <div className="max-w-md mx-auto">
      <div className="px-6 pt-6 pb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold t-text">{t('profile')}</h1>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} data-testid="profile-theme-toggle" className="p-2 rounded-full glass t-text-secondary">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button onClick={toggleLang} data-testid="profile-lang-toggle" className="flex items-center gap-1 px-2 py-1 rounded-full glass text-xs t-text-secondary">
            <Globe className="w-3 h-3" />{lang === 'en' ? 'ID' : 'EN'}
          </button>
        </div>
      </div>

      <div className="px-6">
        {/* NEW-MEMBER WAITING BANNER — shows only while the account has not yet claimed a profile */}
        {!claimedProfile && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="new-member-waiting-banner"
            className="mb-6 rounded-2xl overflow-hidden border-2"
            style={{
              borderColor: '#F59E0B',
              background: 'linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(217,138,148,0.10) 100%)'
            }}
          >
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-11 h-11 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-lg sm:text-xl font-bold t-text font-romantic">
                      {lang === 'en' ? 'Welcome, new member!' : 'Selamat datang, anggota baru!'}
                    </h3>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F59E0B] text-black uppercase tracking-wider">
                      {lang === 'en' ? 'Please read' : 'Harap dibaca'}
                    </span>
                  </div>
                  <p className="text-sm t-text-secondary leading-relaxed font-body">
                    {lang === 'en' ? (
                      <>
                        If you just registered, please wait <span className="font-bold t-text">3–14 working days</span> for your data to appear on this website. You will only be able to <span className="font-bold t-text">claim your profile</span> after that waiting period.
                      </>
                    ) : (
                      <>
                        Jika Anda anggota baru, Anda perlu menunggu <span className="font-bold t-text">3–14 hari kerja</span> hingga data Anda muncul di website ini. Anda baru bisa <span className="font-bold t-text">klaim profil Anda</span> setelah 3–14 hari kerja.
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="rounded-xl p-3 flex items-start gap-2.5 bg-red-500/10 border border-red-500/30">
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm font-bold text-red-600 dark:text-red-400 font-body">
                  {lang === 'en'
                    ? 'DO NOT claim another person\u2019s profile!'
                    : 'JANGAN klaim profil orang lain!'}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Account info */}
        <div className="glass-card rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-full bg-[#D98A94]/10 flex items-center justify-center">
              <User className="w-7 h-7 text-[#D98A94]" />
            </div>
            <div>
              <h2 className="text-lg font-bold t-text">{member?.name || member?.email}</h2>
              <p className="t-text-muted text-sm">{member?.email}</p>
            </div>
          </div>

          {claimedProfile ? (
            <div className="flex items-center gap-2 text-green-500 text-sm">
              <Check className="w-4 h-4" />
              <span>{lang === 'en' ? 'Profile claimed' : 'Profil diklaim'}</span>
            </div>
          ) : (
            <Button onClick={openClaimDialog} data-testid="claim-profile-btn" className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full btn-glow">
              <Heart className="w-4 h-4 mr-2" />{t('claim_profile')}
            </Button>
          )}
        </div>

        {/* Claimed Profile */}
        {claimedProfile && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {/* Photos - editable by member */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <p className="t-text-muted text-xs uppercase tracking-wider">{lang === 'en' ? 'Photos (min 1, max 3)' : 'Foto (min 1, maks 3)'}</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2].map(i => {
                  const photo = claimedProfile.photos?.[i];
                  return (
                    <div key={i} className="relative">
                      <button
                        onClick={() => openPhotoCrop(i)}
                        data-testid={`member-photo-${i}`}
                        className={`w-full aspect-[3/4] rounded-2xl overflow-hidden border-2 border-dashed ${photo ? 'border-[#D98A94]/50' : 'border-[var(--border-color)]'}`}
                      >
                        {photo ? (
                          <img src={photo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1 t-text-muted">
                            <Camera className="w-5 h-5" />
                            <span className="text-[10px]">{lang === 'en' ? 'Upload' : 'Unggah'}</span>
                          </div>
                        )}
                      </button>
                      {photo && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeletePhoto(i); }}
                          data-testid={`member-photo-delete-${i}`}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Basic Info (admin-managed, read-only) */}
            <div className="glass-card rounded-2xl p-6 mb-4">
              <h3 className="text-xl font-bold t-text mb-1">{claimedProfile.name || member?.name}, <span className="t-text-secondary">{claimedProfile.age}</span></h3>
              <div className="flex flex-wrap gap-1.5 mb-4">
                <span className="text-xs text-[#D98A94] bg-[#D98A94]/10 px-2 py-0.5 rounded-full">{claimedProfile.category}</span>
                {claimedProfile.marriage_status && <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">{getMarriageLabel(claimedProfile.marriage_status)}</span>}
                {claimedProfile.tujuan_join && <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">{getTujuanLabel(claimedProfile.tujuan_join)}</span>}
              </div>
              {claimedProfile.bio && <p className="t-text-secondary text-sm mb-4">{claimedProfile.bio}</p>}
              <div className="space-y-2">
                {claimedProfile.occupation && <div className="flex items-center gap-2 t-text-muted text-sm"><Briefcase className="w-4 h-4" /><span>{claimedProfile.occupation}</span></div>}
                {claimedProfile.education && <div className="flex items-center gap-2 t-text-muted text-sm"><GraduationCap className="w-4 h-4" /><span>{claimedProfile.education}</span></div>}
                {claimedProfile.domicile && <div className="flex items-center gap-2 t-text-muted text-sm"><MapPin className="w-4 h-4" /><span>{claimedProfile.domicile}</span></div>}
              </div>
            </div>

            {/* Additional Info (member-editable) */}
            <div className="glass-card rounded-2xl p-6 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="t-text font-semibold text-sm">{lang === 'en' ? 'Additional Information' : 'Informasi Tambahan'}</h4>
                <button onClick={openEditDialog} data-testid="edit-additional-btn" className="flex items-center gap-1 text-[#D98A94] text-xs hover:underline">
                  <Pencil className="w-3 h-3" />{lang === 'en' ? 'Edit' : 'Ubah'}
                </button>
              </div>
              <div className="space-y-0">
                <InfoItem label="MBTI" value={claimedProfile.mbti} />
                <InfoItem label={lang === 'en' ? 'Zodiac' : 'Zodiak'} value={claimedProfile.zodiac} />
                <InfoItem label="Shio" value={claimedProfile.shio} />
                <InfoItem label={lang === 'en' ? 'Religious Sect' : 'Aliran Agama'} value={claimedProfile.aliran_agama} />
                <InfoItem label="Hobby" value={claimedProfile.hobby} />
                <InfoItem label={lang === 'en' ? 'Preferred Places' : 'Tempat Favorit'} value={claimedProfile.preferred_places} />
                <InfoItem label={lang === 'en' ? 'Topics to Talk' : 'Topik Pembicaraan'} value={claimedProfile.topics_to_talk} />
                <InfoItem label={lang === 'en' ? 'Monthly Income' : 'Pendapatan Bulanan'} value={claimedProfile.income_range} />
                <InfoItem label={lang === 'en' ? 'Partner Criteria' : 'Kriteria Pasangan'} value={claimedProfile.kriteria_pasangan} />
                {!claimedProfile.mbti && !claimedProfile.zodiac && !claimedProfile.shio && !claimedProfile.hobby && (
                  <p className="t-text-muted text-xs text-center py-4">{lang === 'en' ? 'No additional info yet. Tap Edit to add.' : 'Belum ada info tambahan. Ketuk Ubah untuk menambahkan.'}</p>
                )}
              </div>
            </div>

            {/* Social links */}
            <div className="glass-card rounded-2xl p-6 mb-4">
              <div className="space-y-3">
                {claimedProfile.whatsapp && (
                  <a href={`https://wa.me/${claimedProfile.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" data-testid="profile-wa-link"
                    className="flex items-center gap-3 t-text-secondary hover:text-green-500 transition">
                    <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center"><Phone className="w-4 h-4 text-green-500" /></div>
                    <span className="text-sm">{claimedProfile.whatsapp}</span>
                    <ExternalLink className="w-3 h-3 ml-auto" />
                  </a>
                )}
                {claimedProfile.instagram && (
                  <a href={`https://instagram.com/${claimedProfile.instagram}`} target="_blank" rel="noopener noreferrer" data-testid="profile-ig-link"
                    className="flex items-center gap-3 t-text-secondary hover:text-purple-500 transition">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center"><ExternalLink className="w-4 h-4 text-purple-500" /></div>
                    <span className="text-sm">{claimedProfile.instagram}</span>
                    <ExternalLink className="w-3 h-3 ml-auto" />
                  </a>
                )}
              </div>
            </div>

            <p className="text-center t-text-muted text-xs mb-4">{lang === 'en' ? 'Basic profile data is managed by admin. You can edit additional info.' : 'Data profil dasar dikelola admin. Anda dapat mengubah info tambahan.'}</p>
          </motion.div>
        )}

        <Button onClick={handleLogout} data-testid="logout-btn" variant="ghost" className="w-full text-red-500 hover:text-red-400 hover:bg-red-500/10 rounded-full">
          <LogOut className="w-4 h-4 mr-2" />{t('logout')}
        </Button>
      </div>

      {/* Claim Profile Dialog - Step Wizard */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent className="glass-card t-text w-[calc(100vw-1rem)] max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="t-text flex items-center gap-3">
              <Heart className="w-5 h-5 text-[#D98A94]" />
              {lang === 'en' ? 'Claim Your Profile' : 'Klaim Profil Anda'}
            </DialogTitle>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-2 mb-4">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${claimStep >= s ? 'bg-[#D98A94] text-white' : 't-bg-input t-text-muted'}`}>
                  {claimStep > s ? <Check className="w-3.5 h-3.5" /> : s}
                </div>
                {s < 3 && <div className={`flex-1 h-0.5 rounded-full transition-all ${claimStep > s ? 'bg-[#D98A94]' : 'bg-[var(--border-color)]'}`} />}
              </div>
            ))}
          </div>

          {/* Step 1: Enter Name */}
          {claimStep === 1 && (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-[#D98A94]/10 flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-[#D98A94]" />
                </div>
                <h3 className="t-text font-semibold text-lg mb-1">{lang === 'en' ? 'What\'s your name?' : 'Siapa nama Anda?'}</h3>
                <p className="t-text-muted text-sm">{lang === 'en' ? 'Enter your full name to find your profile' : 'Masukkan nama lengkap untuk menemukan profil Anda'}</p>
              </div>
              <Input data-testid="claim-name-input" value={claimName} onChange={e => setClaimName(e.target.value)}
                className="t-bg-input t-border h-14 rounded-xl t-text text-center text-lg" placeholder={t('name')}
                onKeyDown={e => e.key === 'Enter' && claimName.trim() && setClaimStep(2)} />
              <div className="flex gap-3">
                <Button onClick={() => setShowClaimDialog(false)} variant="outline" className="flex-1 rounded-full t-border t-text-secondary" data-testid="claim-cancel-btn">{t('cancel')}</Button>
                <Button onClick={() => setClaimStep(2)} disabled={!claimName.trim()} data-testid="claim-next-btn" className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full disabled:opacity-50">
                  {lang === 'en' ? 'Next' : 'Lanjut'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Search & Select Profile */}
          {claimStep === 2 && (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <div>
                <h3 className="t-text font-semibold mb-1">{lang === 'en' ? 'Find your profile' : 'Temukan profil Anda'}</h3>
                <p className="t-text-muted text-xs mb-3">
                  {lang === 'en'
                    ? 'Search by your WhatsApp number — this is the most reliable way. You can also type a city or category.'
                    : 'Cari berdasarkan nomor WhatsApp Anda — ini cara paling akurat. Boleh juga ketik kota atau kategori.'}
                </p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 t-text-muted" />
                  <Input
                    data-testid="claim-search-input"
                    value={claimSearch}
                    onChange={e => setClaimSearch(e.target.value)}
                    className="t-bg-input t-border h-10 rounded-xl t-text pl-10 text-sm"
                    placeholder={lang === 'en' ? 'WhatsApp number e.g. 81234567890' : 'Nomor WhatsApp mis. 81234567890'}
                    inputMode="tel"
                  />
                </div>
              </div>

              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {claimLoading && (
                  <div data-testid="claim-loading" className="flex items-center justify-center gap-2 py-6 t-text-muted text-sm">
                    <div className="w-4 h-4 rounded-full border-2 border-[#D98A94]/30 border-t-[#D98A94] animate-spin" />
                    <span>{lang === 'en' ? 'Searching…' : 'Mencari…'}</span>
                  </div>
                )}
                {!claimLoading && claimResults.map(p => {
                    const photo = p.photos?.find(ph => ph) || '';
                    const isSelected = selectedToClaim?.id === p.id;
                    return (
                      <button key={p.id} onClick={() => setSelectedToClaim(p)} data-testid={`claim-option-${p.id}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${isSelected ? 'bg-[#D98A94]/10 border border-[#D98A94]/40 shadow-sm' : 'glass hover:bg-[var(--bg-input)]'}`}>
                        <div className="w-14 h-14 rounded-xl overflow-hidden bg-[var(--bg-input)] flex-shrink-0">
                          {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-6 h-6 t-text-muted" /></div>}
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <p className="t-text text-sm font-semibold truncate">{p.name || `${p.gender === 'male' ? 'M' : 'F'}, ${p.age}`}</p>
                          <p className="t-text-muted text-xs truncate">{p.whatsapp || p.domicile}</p>
                          <span className="text-[10px] bg-[#D98A94]/10 text-[#D98A94] px-1.5 py-0.5 rounded-full">{p.category}</span>
                        </div>
                        {isSelected && <Check className="w-5 h-5 text-[#D98A94] flex-shrink-0" />}
                      </button>
                    );
                  })}
                {!claimLoading && claimResults.length === 0 && (
                  <p className="t-text-muted text-sm text-center py-8">{lang === 'en' ? 'No profiles found' : 'Tidak ada profil ditemukan'}</p>
                )}
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setClaimStep(1)} variant="outline" className="flex-1 rounded-full t-border t-text-secondary">{lang === 'en' ? 'Back' : 'Kembali'}</Button>
                <Button onClick={() => setClaimStep(3)} disabled={!selectedToClaim} data-testid="claim-preview-btn" className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full disabled:opacity-50">
                  {lang === 'en' ? 'Preview' : 'Pratinjau'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Confirm */}
          {claimStep === 3 && selectedToClaim && (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <div className="text-center mb-2">
                <h3 className="t-text font-semibold mb-1">{lang === 'en' ? 'Is this your profile?' : 'Apakah ini profil Anda?'}</h3>
                <p className="t-text-muted text-xs">{lang === 'en' ? 'Please confirm this is you before claiming' : 'Konfirmasi bahwa ini adalah profil Anda'}</p>
              </div>

              {/* Preview card */}
              <div className="glass-card rounded-2xl overflow-hidden">
                {selectedToClaim.photos?.find(p => p) && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={selectedToClaim.photos.find(p => p)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-[#D98A94]/10 text-[#D98A94] px-2 py-0.5 rounded-full">{selectedToClaim.category}</span>
                    <span className="text-xs t-bg-input t-text-secondary px-2 py-0.5 rounded-full capitalize">{selectedToClaim.gender}</span>
                  </div>
                  <h4 className="t-text font-bold text-lg">{selectedToClaim.name || `${selectedToClaim.gender}, ${selectedToClaim.age}`}</h4>
                  <p className="t-text-secondary text-sm">{selectedToClaim.age} {lang === 'en' ? 'years old' : 'tahun'} - {selectedToClaim.domicile}</p>
                  {selectedToClaim.occupation && <p className="t-text-muted text-xs mt-1">{selectedToClaim.occupation}</p>}
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                    <p className="t-text-muted text-xs">{lang === 'en' ? 'Claiming as:' : 'Diklaim sebagai:'}</p>
                    <p className="t-text font-semibold">{claimName}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setClaimStep(2)} variant="outline" className="flex-1 rounded-full t-border t-text-secondary">{lang === 'en' ? 'Back' : 'Kembali'}</Button>
                <Button onClick={handleClaim} disabled={loading} data-testid="claim-submit-btn" className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full btn-glow disabled:opacity-50">
                  {loading ? '...' : (lang === 'en' ? 'Confirm & Claim' : 'Konfirmasi & Klaim')}
                </Button>
              </div>
            </motion.div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Additional Info Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="glass-card t-text w-[calc(100vw-1rem)] max-w-3xl max-h-[88vh] overflow-y-auto p-4 sm:p-5 rounded-2xl">
          <DialogHeader className="pb-2 mb-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#D98A94]/15 flex items-center justify-center">
                <User className="w-4 h-4 text-[#D98A94]" />
              </div>
              <div>
                <DialogTitle className="t-text text-base">{lang === 'en' ? 'Edit Additional Info' : 'Ubah Info Tambahan'}</DialogTitle>
                <p className="text-[10px] t-text-muted mt-0.5">{lang === 'en' ? 'These help others match you better.' : 'Info ini membantu match Anda lebih akurat.'}</p>
              </div>
            </div>
          </DialogHeader>

          {/* Section: Data Diri — Domisili, Usia, Pendidikan (member self-edit) */}
          <div className="mb-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <MapPin className="w-3 h-3 text-[#D98A94]" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#D98A94]">{lang === 'en' ? 'Personal Info' : 'Data Diri'}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold mb-0.5 block">{lang === 'en' ? 'Country' : 'Negara'}</label>
                <Input data-testid="edit-country" value={editForm.country || ''} onChange={e => setEditForm({...editForm, country: e.target.value})} className={compactInputCls} placeholder="Indonesia" />
              </div>
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold mb-0.5 block">{lang === 'en' ? 'City / Domicile' : 'Kota / Domisili'}</label>
                <Input data-testid="edit-city" value={editForm.city || ''} onChange={e => setEditForm({...editForm, city: e.target.value})} className={compactInputCls} placeholder={lang === 'en' ? 'Jakarta' : 'Jakarta'} />
              </div>
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold mb-0.5 block">{lang === 'en' ? 'Birth Year (age)' : 'Tahun Lahir (usia)'}</label>
                <Input data-testid="edit-birth-year" type="number" inputMode="numeric" min="1926" max="2009"
                  value={editForm.birth_year || ''}
                  onChange={e => {
                    const v = e.target.value.replace(/[^0-9]/g, '');
                    setEditForm({...editForm, birth_year: v ? parseInt(v, 10) : ''});
                  }}
                  className={compactInputCls} placeholder="1995" />
                {editForm.birth_year && (
                  <p className="text-[9px] text-[#D98A94] mt-0.5">{lang === 'en' ? 'Age' : 'Usia'}: {new Date().getFullYear() - Number(editForm.birth_year)} {lang === 'en' ? 'yr' : 'thn'}</p>
                )}
              </div>
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold mb-0.5 block">{lang === 'en' ? 'Education' : 'Pendidikan'}</label>
                <Input data-testid="edit-education" value={editForm.education || ''} onChange={e => setEditForm({...editForm, education: e.target.value})} className={compactInputCls} placeholder={lang === 'en' ? 'S1 - Bachelor / Institution' : 'S1 - Universitas Indonesia'} />
              </div>
            </div>
          </div>

          {/* Section: Personality — 3 cols on sm+ */}
          <div className="mb-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Sparkles className="w-3 h-3 text-[#D98A94]" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#D98A94]">{lang === 'en' ? 'Personality' : 'Kepribadian'}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold">MBTI</label>
                  <a href={FIELD_LINKS.mbti} target="_blank" rel="noopener noreferrer" className="text-[9px] text-[#D98A94] hover:underline flex items-center gap-0.5"><Link2 className="w-2.5 h-2.5" />{lang === 'en' ? 'Test' : 'Tes'}</a>
                </div>
                <Select value={editForm.mbti || 'none'} onValueChange={v => setEditForm({...editForm, mbti: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-mbti" className={compactInputCls}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="glass-card t-text">
                    <SelectItem value="none" className="t-text">-</SelectItem>
                    {MBTI_OPTIONS.map(m => <SelectItem key={m} value={m} className="t-text">{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold">{lang === 'en' ? 'Zodiac' : 'Zodiak'}</label>
                  <a href={FIELD_LINKS.zodiac} target="_blank" rel="noopener noreferrer" className="text-[9px] text-[#D98A94] hover:underline flex items-center gap-0.5"><Link2 className="w-2.5 h-2.5" />{lang === 'en' ? 'Find' : 'Cari'}</a>
                </div>
                <Select value={editForm.zodiac || 'none'} onValueChange={v => setEditForm({...editForm, zodiac: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-zodiac" className={compactInputCls}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="glass-card t-text">
                    <SelectItem value="none" className="t-text">-</SelectItem>
                    {ZODIAC_OPTIONS.map(z => <SelectItem key={z} value={z} className="t-text">{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold">Shio</label>
                  <a href={FIELD_LINKS.shio} target="_blank" rel="noopener noreferrer" className="text-[9px] text-[#D98A94] hover:underline flex items-center gap-0.5"><Link2 className="w-2.5 h-2.5" />{lang === 'en' ? 'Find' : 'Cari'}</a>
                </div>
                <Select value={editForm.shio || 'none'} onValueChange={v => setEditForm({...editForm, shio: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-shio" className={compactInputCls}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="glass-card t-text">
                    <SelectItem value="none" className="t-text">-</SelectItem>
                    {SHIO_OPTIONS.map(s => <SelectItem key={s} value={s} className="t-text">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section: Life Values — 2 cols */}
          <div className="mb-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Heart className="w-3 h-3 text-[#D98A94]" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#D98A94]">{lang === 'en' ? 'Values' : 'Nilai Hidup'}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold block mb-0.5">{lang === 'en' ? 'Religious Sect' : 'Aliran Agama'}</label>
                <Select value={editForm.aliran_agama || 'none'} onValueChange={v => setEditForm({...editForm, aliran_agama: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-aliran-agama" className={compactInputCls}><SelectValue placeholder={lang === 'en' ? 'Select' : 'Pilih'} /></SelectTrigger>
                  <SelectContent className="glass-card t-text max-h-[250px]">
                    <SelectItem value="none" className="t-text">-</SelectItem>
                    {aliranOptions.map(a => <SelectItem key={a} value={a} className="t-text">{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold block mb-0.5">{lang === 'en' ? 'Marriage Status' : 'Status Nikah'}</label>
                <Select value={editForm.marriage_status || 'none'} onValueChange={v => setEditForm({...editForm, marriage_status: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-marriage-status" className={compactInputCls}><SelectValue placeholder={lang === 'en' ? 'Select' : 'Pilih'} /></SelectTrigger>
                  <SelectContent className="glass-card t-text">
                    <SelectItem value="none" className="t-text">-</SelectItem>
                    {MARRIAGE_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="t-text">{lang === 'en' ? o.en : o.id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold block mb-0.5">{lang === 'en' ? 'Purpose of Joining' : 'Tujuan Bergabung'}</label>
                <Select value={editForm.tujuan_join || 'none'} onValueChange={v => setEditForm({...editForm, tujuan_join: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-tujuan" className={compactInputCls}><SelectValue placeholder={lang === 'en' ? 'Select' : 'Pilih'} /></SelectTrigger>
                  <SelectContent className="glass-card t-text">
                    <SelectItem value="none" className="t-text">-</SelectItem>
                    {TUJUAN_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="t-text">{lang === 'en' ? o.en : o.id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold block mb-0.5">{lang === 'en' ? 'Monthly Income' : 'Pendapatan / Bln'}</label>
                <Select value={editForm.income_range || 'none'} onValueChange={v => setEditForm({...editForm, income_range: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-income" className={compactInputCls}><SelectValue placeholder={lang === 'en' ? 'Select' : 'Pilih'} /></SelectTrigger>
                  <SelectContent className="glass-card t-text">
                    <SelectItem value="none" className="t-text">-</SelectItem>
                    {INCOME_RANGE_OPTIONS.map(r => <SelectItem key={r} value={r} className="t-text">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section: Interests — stacked */}
          <div className="mb-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Sparkles className="w-3 h-3 text-[#D98A94]" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#D98A94]">{lang === 'en' ? 'Interests' : 'Minat'}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold block mb-0.5">Hobby</label>
                <Input data-testid="edit-hobby" value={editForm.hobby} onChange={e => setEditForm({...editForm, hobby: e.target.value})} className={compactInputCls} placeholder={lang === 'en' ? 'Reading, Hiking...' : 'Membaca, Hiking...'} />
              </div>
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold block mb-0.5">{lang === 'en' ? 'Favorite Places' : 'Tempat Favorit'}</label>
                <Input data-testid="edit-places" value={editForm.preferred_places} onChange={e => setEditForm({...editForm, preferred_places: e.target.value})} className={compactInputCls} placeholder={lang === 'en' ? 'Cafe, Beach...' : 'Cafe, Pantai...'} />
              </div>
              <div>
                <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold block mb-0.5">{lang === 'en' ? 'Topics' : 'Topik'}</label>
                <Input data-testid="edit-topics" value={editForm.topics_to_talk} onChange={e => setEditForm({...editForm, topics_to_talk: e.target.value})} className={compactInputCls} placeholder={lang === 'en' ? 'Tech, Travel...' : 'Teknologi, Travel...'} />
              </div>
            </div>
            <div className="mt-2.5">
              <label className="t-text-secondary text-[10px] uppercase tracking-wider font-semibold block mb-0.5">{lang === 'en' ? 'Partner Criteria' : 'Kriteria Pasangan'}</label>
              <Textarea data-testid="edit-kriteria" value={editForm.kriteria_pasangan} onChange={e => setEditForm({...editForm, kriteria_pasangan: e.target.value})} rows={2} className="t-bg-input t-border rounded-lg text-sm px-3 py-2 t-text min-h-[62px] resize-none" placeholder={lang === 'en' ? 'Describe your ideal partner...' : 'Deskripsikan pasangan ideal Anda...'} />
            </div>
          </div>

          {/* Section: Social */}
          <div className="mb-4">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Link2 className="w-3 h-3 text-[#D98A94]" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#D98A94]">{lang === 'en' ? 'Social Links' : 'Link Sosial'}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <Input value={editForm.instagram} onChange={e => setEditForm({...editForm, instagram: e.target.value.replace(/^@/, '').replace(/^https?:\/\/(www\.)?instagram\.com\/@?/i, '').split('?')[0].split('/')[0]})} className={compactInputCls} placeholder="Instagram username" data-testid="edit-instagram" />
              <Input value={editForm.tiktok} onChange={e => setEditForm({...editForm, tiktok: e.target.value.replace(/^@/, '').replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/i, '').split('?')[0].split('/')[0]})} className={compactInputCls} placeholder="TikTok username" data-testid="edit-tiktok" />
              <Input value={editForm.facebook} onChange={e => setEditForm({...editForm, facebook: e.target.value})} className={compactInputCls} placeholder="Facebook URL" data-testid="edit-facebook" />
            </div>
          </div>

          {/* Section: Extended Optional (Batch 3) */}
          <div className="mb-4">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Sparkles className="w-3 h-3 text-[#D98A94]" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#D98A94]">
                {lang === 'en' ? 'Extended Info (optional)' : 'Informasi Tambahan (Opsional)'}
              </p>
            </div>
            <ExtendedProfileEditor
              value={editForm}
              onFieldChange={(k, v) => setEditForm(prev => ({ ...prev, [k]: v }))}
              lang={lang}
            />
          </div>

          <div className="flex gap-2 pt-3 border-t sticky bottom-0 t-bg" style={{ borderColor: 'var(--border-color)' }}>
            <Button onClick={() => setShowEditDialog(false)} variant="outline" className="flex-1 t-border t-text-secondary rounded-lg h-9" data-testid="edit-cancel-btn">{t('cancel')}</Button>
            <Button onClick={handleSaveEdit} disabled={loading} className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-lg h-9" data-testid="edit-save-btn">
              {loading ? '...' : t('save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ImageCropper open={cropOpen} onClose={() => setCropOpen(false)} onCropComplete={handleCroppedPhoto} lang={lang} />

      </div>
      <BottomNav active="profile" />
    </div>
  );
}
